import React, { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from '@src/helpers/toaster'

import Popup from '@components/Popup'
import Button from '@components/Button'
import Text from '@components/Text'

import { useRevokeTeamInvite } from '@queries/OrgStructure'
import { requestErrorsHandler } from '@helpers/utils'

import type { InviteRef } from './utils'

export interface RevokeInvitePopupProps {
  invite: InviteRef
  onClose(): void
}

// Confirmation modal for revoking a pending invite. Modeled after
// DeleteUserPopup's structure (Popup + header + body + footer buttons)
// so this slot into the same design language as the rest of the Team
// page's destructive actions.
export const RevokeInvitePopup: React.FC<RevokeInvitePopupProps> = ({ invite, onClose }) => {
  const { t } = useTranslation('org', { keyPrefix: 'revokeInvite' })
  const { mutateAsync: revoke, isLoading } = useRevokeTeamInvite(invite.uuid)

  const handleConfirm = useCallback(async () => {
    try {
      await revoke({})
      onClose()
      toast.success(t('successToast'))
    } catch (err) {
      // Fallback copy goes INTO the handler — it already raises a toast of its
      // own, so a separate toast.error here stacked two on one failure.
      requestErrorsHandler(err, t('errorToast'))
    }
  }, [revoke, onClose, t])

  return (
    <Popup containerStyle="z-nested" className="p-6" onClose={onClose}>
      <div className="flex flex-col mb-4 gap-3">
        {/* ``<Text>`` renders a ``<div>`` containing ``<p>`` elements;
            nesting it inside a ``<p>`` produces invalid HTML that some
            browsers auto-close in surprising places. Use ``<div>``
            wrappers and keep the same Tailwind classes. */}
        <div className="font-medium border-b leading-8">
          <Text text={t('title')} />
        </div>
        <div>
          {/* ``<Trans>`` rather than ``<Text>`` so the recipient stands out on a
              destructive confirm — ``<Text>`` only bolds whole space-separated
              tokens, and the trailing "'s" would swallow the marker. */}
          <p>
            <Trans
              t={t}
              i18nKey="confirmText"
              values={{ email: invite.email }}
              components={[<strong className="font-semibold" key="0" />]}
            />
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="outlined" buttonType="button" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button color="cancel" buttonType="button" onClick={handleConfirm} loading={isLoading}>
          {t('confirm')}
        </Button>
      </div>
    </Popup>
  )
}

export default RevokeInvitePopup
